import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut as firebaseSignOut, type User as FirebaseUser } from 'firebase/auth';
import { db, auth } from './app';
import type {
  User,
  PromoCode,
  License,
  WalletTransaction,
  LicensePlan,
  PromoCodeUsage,
  SupportTicket,
  SupportResponse,
  AdminSettings
} from '../../types';

// Authentication functions
export const signInWithGoogle = async (): Promise<void> => {
  try {
    const provider = new GoogleAuthProvider();
    
    // Configure the provider for better popup behavior
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    // Use signInWithPopup with proper error handling
    const result = await signInWithPopup(auth, provider);
    console.log('Google sign-in successful:', result.user?.email);
  } catch (error: any) {
    console.error('Google sign-in error:', error);
    
    // Handle specific popup errors
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled. Please try again.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked by browser. Please allow popups for this site and try again.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Sign-in was cancelled due to another popup being opened.');
    } else {
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }
};

export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// User Management
export const createUserProfile = async (userData: Omit<User, 'createdAt' | 'updatedAt' | 'defaultPromoCode'>) => {
  try {
    // Get admin settings before starting the transaction
    const adminSettings = await getAdminSettings();
    
    const defaultDiscount = adminSettings?.defaultPromoDiscount || 5;
    const defaultReward = adminSettings?.defaultPromoReward || 50;
    const defaultUsageLimit = adminSettings?.defaultPromoUsageLimit || 100;
    const defaultValidityDays = adminSettings?.defaultPromoValidityDays || 365;
    
    const userRef = doc(db, 'users', userData.uid);
    
    // Use a transaction to ensure atomicity and prevent duplicate creation
    await runTransaction(db, async (transaction) => {
      // Check if user already exists
      const userDoc = await transaction.get(userRef);
      if (userDoc.exists()) {
        return;
      }
      
      // Generate a deterministic default promo code for the user
      const defaultPromoCode = generateDefaultUserCode(userData.uid);
      
      // Check if promo code already exists
      const promoCodesRef = collection(db, 'promoCodes');
      const existingPromoQuery = query(
        promoCodesRef,
        where('code', '==', defaultPromoCode),
        where('createdBy', '==', userData.uid)
      );
      const existingPromoSnapshot = await getDocs(existingPromoQuery);
      
      if (!existingPromoSnapshot.empty) {
        // Just create the user profile without the promo code
        const userDataWithDefaults = {
          ...userData,
          defaultPromoCode,
          isActive: true, // New users are active by default
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        transaction.set(userRef, userDataWithDefaults);
        return;
      }
      
      // Create user profile
      const userDataWithDefaults = {
        ...userData,
        defaultPromoCode,
        isActive: true, // New users are active by default
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      transaction.set(userRef, userDataWithDefaults);
      
      // Create the default promo code with admin-configured values
      const promoCodeRef = doc(collection(db, 'promoCodes'));
      const promoCodeData = {
        code: defaultPromoCode,
        createdBy: userData.uid,
        discountPercentage: defaultDiscount,
        rewardAmount: defaultReward,
        usageLimit: defaultUsageLimit,
        usedCount: 0,
        isActive: true,
        isDefault: true, // Mark as default user code
        validUntil: new Date(Date.now() + defaultValidityDays * 24 * 60 * 60 * 1000),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      transaction.set(promoCodeRef, promoCodeData);
    });
    
    return userRef.id;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as unknown as User;
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, updates: Partial<User>) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Promo Code Management (Admin Only)
export const createPromoCode = async (
  promoData: Omit<PromoCode, 'id' | 'createdAt' | 'updatedAt'>, 
  userRole: string
) => {
  try {
    // Enforce admin-only promo code creation
    if (userRole !== 'admin') {
      throw new Error('Only administrators can create custom promo codes');
    }
    
    const promoRef = await addDoc(collection(db, 'promoCodes'), {
      ...promoData,
      isDefault: false, // Admin-created codes are not default user codes
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return promoRef.id;
  } catch (error) {
    console.error('Error creating promo code:', error);
    throw error;
  }
};

export const getPromoCode = async (code: string): Promise<PromoCode | null> => {
  try {
    const q = query(
      collection(db, 'promoCodes'),
      where('code', '==', code),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as PromoCode;
    }
    return null;
  } catch (error) {
    console.error('Error getting promo code:', error);
    throw error;
  }
};

export const getUserPromoCodes = async (uid: string): Promise<PromoCode[]> => {
  try {
    const q = query(
      collection(db, 'promoCodes'),
      where('createdBy', '==', uid),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as PromoCode;
    });
  } catch (error) {
    console.error('Error getting user promo codes:', error);
    throw error;
  }
};

// Promo Code Usage
export const usePromoCode = async (
  promoCodeId: string,
  usedBy: string,
  orderId: string,
  discountAmount: number
): Promise<string> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Get promo code
      const promoRef = doc(db, 'promoCodes', promoCodeId);
      const promoDoc = await transaction.get(promoRef);
      
      if (!promoDoc.exists()) {
        throw new Error('Promo code not found');
      }
      
      const promo = promoDoc.data() as PromoCode;
      
      // Check if user can use this promo code
      if (promo.createdBy === usedBy) {
        throw new Error('Cannot use your own promo code');
      }
      
      // Check usage limit
      if (promo.usedCount >= promo.usageLimit) {
        throw new Error('Promo code usage limit exceeded');
      }
      
      // Check if user already used this promo code
      const existingUsageQuery = query(
        collection(db, 'promoCodeUsages'),
        where('promoCodeId', '==', promoCodeId),
        where('usedBy', '==', usedBy)
      );
      const existingUsage = await getDocs(existingUsageQuery);
      
      if (!existingUsage.empty) {
        throw new Error('You have already used this promo code');
      }
      
      // Create usage record
      const usageRef = doc(collection(db, 'promoCodeUsages'));
      transaction.set(usageRef, {
        promoCodeId,
        promoCode: promo.code,
        usedBy,
        createdBy: promo.createdBy,
        discountAmount,
        rewardAmount: promo.rewardAmount,
        orderId,
        usedAt: serverTimestamp()
      });
      
      // Update promo code usage count
      transaction.update(promoRef, {
        usedCount: promo.usedCount + 1,
        updatedAt: serverTimestamp()
      });
      
      // Add reward to promo code creator's wallet
      const creatorRef = doc(db, 'users', promo.createdBy);
      const creatorDoc = await transaction.get(creatorRef);
      
      if (creatorDoc.exists()) {
        const creator = creatorDoc.data() as User;
        transaction.update(creatorRef, {
          walletBalance: creator.walletBalance + promo.rewardAmount,
          updatedAt: serverTimestamp()
        });
        
        // Create wallet transaction for creator
        const walletTransactionRef = doc(collection(db, 'walletTransactions'));
        transaction.set(walletTransactionRef, {
          userId: promo.createdBy,
          type: 'credit',
          amount: promo.rewardAmount,
          description: `Reward for promo code usage: ${promo.code}`,
          referenceId: usageRef.id,
          createdAt: serverTimestamp()
        });
      }
      
      return usageRef.id;
    });
  } catch (error) {
    console.error('Error using promo code:', error);
    throw error;
  }
};

// Enhanced License Purchase with Wallet Balance Support
export const purchaseLicenseWithWalletAndPromoCode = async (
  licenseData: Omit<License, 'id' | 'licenseKey' | 'purchasedAt' | 'paymentTransactionId' | 'walletAmountUsed' | 'remainingAmount'>,
  useWalletBalance: boolean = false,
  promoCodeToUse?: string
) => {
  try {
    // Validate license purchase before starting transaction
    const isTrialPlan = licenseData.pricePerLicense === 0;
    const purchaseValidation = await validateLicensePurchase(
      licenseData.purchasedBy,
      licenseData.gstNumber,
      isTrialPlan
    );
    
    if (!purchaseValidation.valid) {
      throw new Error(purchaseValidation.message);
    }

    return await runTransaction(db, async (transaction) => {
      let promoCode = null;
      let promoCodeDoc = null;
      let purchaserUser = null;
      let purchaserUserDoc = null;
      
      // Read purchaser's user data to check wallet balance
      const purchaserUserRef = doc(db, 'users', licenseData.purchasedBy);
      purchaserUserDoc = await transaction.get(purchaserUserRef);
      if (purchaserUserDoc.exists()) {
        purchaserUser = purchaserUserDoc.data() as User;
      }
      
      // First, read all data we need (must be done before any writes)
      if (promoCodeToUse) {
        const promoQuery = query(
          collection(db, 'promoCodes'),
          where('code', '==', promoCodeToUse),
          where('isActive', '==', true)
        );
        const promoSnapshot = await getDocs(promoQuery);
        
        if (promoSnapshot.empty) {
          throw new Error('Invalid promo code');
        }
        
        promoCodeDoc = promoSnapshot.docs[0];
        promoCode = promoCodeDoc.data() as PromoCode;
        
        // Validate promo code
        if (!promoCode.isActive) {
          throw new Error('Promo code is inactive');
        }
        
        if (promoCode.usedCount >= promoCode.usageLimit) {
          throw new Error('Promo code usage limit exceeded');
        }
        
        if (new Date(promoCode.validUntil) < new Date()) {
          throw new Error('Promo code has expired');
        }
        
        if (promoCode.createdBy === licenseData.purchasedBy) {
          throw new Error('Cannot use your own promo code');
        }
        
        // Check if user already used this promo code
        const existingUsageQuery = query(
          collection(db, 'promoCodeUsages'),
          where('promoCodeId', '==', promoCodeDoc.id),
          where('usedBy', '==', licenseData.purchasedBy)
        );
        const existingUsage = await getDocs(existingUsageQuery);
        
        if (!existingUsage.empty) {
          throw new Error('You have already used this promo code');
        }
      }
      
      // Read promo code creator's user data for wallet update
      let promoCreatorUser = null;
      let promoCreatorUserDoc = null;
      if (promoCode) {
        const creatorUserRef = doc(db, 'users', promoCode.createdBy);
        promoCreatorUserDoc = await transaction.get(creatorUserRef);
        if (promoCreatorUserDoc.exists()) {
          promoCreatorUser = promoCreatorUserDoc.data() as User;
        }
      }
      
      // Calculate wallet usage and remaining amount
      let walletAmountUsed = 0;
      let remainingAmount = licenseData.finalAmount;
      
      if (useWalletBalance && purchaserUser && purchaserUser.walletBalance > 0) {
        walletAmountUsed = Math.min(purchaserUser.walletBalance, licenseData.finalAmount);
        remainingAmount = licenseData.finalAmount - walletAmountUsed;
      }
      
      // Now perform all writes
      const licenseKey = generateLicenseKey();
      const paymentTransactionId = generatePaymentTransactionId();
      
      // Clean license data - remove undefined fields
      const cleanLicenseData = Object.fromEntries(
        Object.entries(licenseData).filter(([, value]) => value !== undefined)
      );
      
      // Build license object with wallet information (only include if used)
      const licenseDataToSave: Record<string, unknown> = {
        ...cleanLicenseData,
        licenseKey,
        paymentTransactionId,
        purchasedAt: serverTimestamp()
      };
      
      // Only add wallet fields if wallet was actually used
      if (walletAmountUsed > 0) {
        licenseDataToSave.walletAmountUsed = walletAmountUsed;
        licenseDataToSave.remainingAmount = remainingAmount;
      }
      
      // Create license
      const licenseRef = doc(collection(db, 'licenses'));
      transaction.set(licenseRef, licenseDataToSave);
      
      // Deduct wallet balance if used
      if (walletAmountUsed > 0 && purchaserUser && purchaserUserDoc) {
        transaction.update(purchaserUserDoc.ref, {
          walletBalance: purchaserUser.walletBalance - walletAmountUsed,
          updatedAt: serverTimestamp()
        });
        
        // Create wallet transaction for debit
        const walletTransactionRef = doc(collection(db, 'walletTransactions'));
        transaction.set(walletTransactionRef, {
          userId: licenseData.purchasedBy,
          type: 'debit',
          amount: walletAmountUsed,
          description: `License purchase: ${licenseKey}`,
          referenceId: licenseRef.id,
          createdAt: serverTimestamp()
        });
      }
      
      // If promo code is used, handle all promo code related operations
      if (promoCode && promoCodeDoc) {
        // Create promo code usage record
        const usageRef = doc(collection(db, 'promoCodeUsages'));
        transaction.set(usageRef, {
          promoCodeId: promoCodeDoc.id,
          promoCode: promoCode.code,
          usedBy: licenseData.purchasedBy,
          createdBy: promoCode.createdBy,
          discountAmount: licenseData.discountAmount,
          rewardAmount: promoCode.rewardAmount,
          orderId: licenseRef.id,
          usedAt: serverTimestamp()
        });
        
        // Update promo code usage count
        transaction.update(promoCodeDoc.ref, {
          usedCount: promoCode.usedCount + 1,
          updatedAt: serverTimestamp()
        });
        
        // Update wallet balance for promo code creator
        if (promoCreatorUserDoc && promoCreatorUser) {
          transaction.update(promoCreatorUserDoc.ref, {
            walletBalance: promoCreatorUser.walletBalance + promoCode.rewardAmount,
            updatedAt: serverTimestamp()
          });
          
          // Create wallet transaction record for creator
          const creatorWalletTransactionRef = doc(collection(db, 'walletTransactions'));
          transaction.set(creatorWalletTransactionRef, {
            userId: promoCode.createdBy,
            type: 'credit',
            amount: promoCode.rewardAmount,
            description: `Promo code reward: ${promoCode.code}`,
            referenceId: usageRef.id,
            createdAt: serverTimestamp()
          });
        }
      }
      
      return { 
        id: licenseRef.id, 
        licenseKey, 
        paymentTransactionId,
        walletAmountUsed,
        remainingAmount
      };
    });
  } catch (error) {
    console.error('Error purchasing license with wallet and promo code:', error);
    throw error;
  }
};

// License Management with Promo Code Support
export const purchaseLicenseWithPromoCode = async (
  licenseData: Omit<License, 'id' | 'licenseKey' | 'purchasedAt' | 'paymentTransactionId'>,
  promoCodeToUse?: string
) => {
  try {
    // Validate license purchase before starting transaction
    const isTrialPlan = licenseData.pricePerLicense === 0;
    const purchaseValidation = await validateLicensePurchase(
      licenseData.purchasedBy,
      licenseData.gstNumber,
      isTrialPlan
    );
    
    if (!purchaseValidation.valid) {
      throw new Error(purchaseValidation.message);
    }

    return await runTransaction(db, async (transaction) => {
      let promoCode = null;
      let promoCodeDoc = null;
      
      // First, read all data we need (must be done before any writes)
      if (promoCodeToUse) {
        const promoQuery = query(
          collection(db, 'promoCodes'),
          where('code', '==', promoCodeToUse),
          where('isActive', '==', true)
        );
        const promoSnapshot = await getDocs(promoQuery);
        
        if (promoSnapshot.empty) {
          throw new Error('Invalid promo code');
        }
        
        promoCodeDoc = promoSnapshot.docs[0];
        promoCode = promoCodeDoc.data() as PromoCode;
        
        // Validate promo code
        if (!promoCode.isActive) {
          throw new Error('Promo code is inactive');
        }
        
        if (promoCode.usedCount >= promoCode.usageLimit) {
          throw new Error('Promo code usage limit exceeded');
        }
        
        if (new Date(promoCode.validUntil) < new Date()) {
          throw new Error('Promo code has expired');
        }
        
        if (promoCode.createdBy === licenseData.purchasedBy) {
          throw new Error('Cannot use your own promo code');
        }
        
        // Check if user already used this promo code
        const existingUsageQuery = query(
          collection(db, 'promoCodeUsages'),
          where('promoCodeId', '==', promoCodeDoc.id),
          where('usedBy', '==', licenseData.purchasedBy)
        );
        const existingUsage = await getDocs(existingUsageQuery);
        
        if (!existingUsage.empty) {
          throw new Error('You have already used this promo code');
        }
      }
      
      // Read user data for wallet update
      let userDoc = null;
      let userData = null;
      if (promoCode) {
        const userRef = doc(db, 'users', promoCode.createdBy);
        userDoc = await transaction.get(userRef);
        if (userDoc.exists()) {
          userData = userDoc.data() as User;
        }
      }
      
      // Now perform all writes
      const licenseKey = generateLicenseKey();
      const paymentTransactionId = generatePaymentTransactionId();
      
      // Clean license data - remove undefined fields
      const cleanLicenseData = Object.fromEntries(
        Object.entries(licenseData).filter(([, value]) => value !== undefined)
      );
      
      // Create license
      const licenseRef = doc(collection(db, 'licenses'));
      transaction.set(licenseRef, {
        ...cleanLicenseData,
        licenseKey,
        paymentTransactionId,
        purchasedAt: serverTimestamp()
      });
      
      // If promo code is used, handle all promo code related operations
      if (promoCode && promoCodeDoc) {
        // Create promo code usage record
        const usageRef = doc(collection(db, 'promoCodeUsages'));
        transaction.set(usageRef, {
          promoCodeId: promoCodeDoc.id,
          promoCode: promoCode.code,
          usedBy: licenseData.purchasedBy,
          createdBy: promoCode.createdBy,
          discountAmount: licenseData.discountAmount,
          rewardAmount: promoCode.rewardAmount,
          orderId: licenseRef.id,
          usedAt: serverTimestamp()
        });
        
        // Update promo code usage count
        transaction.update(promoCodeDoc.ref, {
          usedCount: promoCode.usedCount + 1,
          updatedAt: serverTimestamp()
        });
        
        // Update wallet balance for promo code creator
        if (userDoc && userData) {
          transaction.update(userDoc.ref, {
            walletBalance: userData.walletBalance + promoCode.rewardAmount,
            updatedAt: serverTimestamp()
          });
          
          // Create wallet transaction record
          const walletTransactionRef = doc(collection(db, 'walletTransactions'));
          transaction.set(walletTransactionRef, {
            userId: promoCode.createdBy,
            type: 'credit',
            amount: promoCode.rewardAmount,
            description: `Promo code reward: ${promoCode.code}`,
            referenceId: usageRef.id,
            createdAt: serverTimestamp()
          });
        }
      }
      
      return { id: licenseRef.id, licenseKey, paymentTransactionId };
    });
  } catch (error) {
    console.error('Error purchasing license with promo code:', error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const purchaseLicense = async (licenseData: Omit<License, 'id' | 'licenseKey' | 'purchasedAt' | 'paymentTransactionId'>) => {
  return purchaseLicenseWithPromoCode(licenseData);
};

export const getUserLicenses = async (uid: string): Promise<License[]> => {
  try {
    const q = query(
      collection(db, 'licenses'),
      where('purchasedBy', '==', uid),
      orderBy('purchasedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        purchasedAt: data.purchasedAt?.toDate ? data.purchasedAt.toDate() : new Date(data.purchasedAt),
        activatedAt: data.activatedAt?.toDate ? data.activatedAt.toDate() : (data.activatedAt ? new Date(data.activatedAt) : undefined)
      } as License;
    });
  } catch (error) {
    console.error('Error getting user licenses:', error);
    throw error;
  }
};

export const activateLicense = async (licenseKey: string, userId: string) => {
  try {
    const q = query(
      collection(db, 'licenses'),
      where('licenseKey', '==', licenseKey),
      where('purchasedBy', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('License not found or not owned by user');
    }
    
    const licenseDoc = snapshot.docs[0];
    await updateDoc(licenseDoc.ref, {
      activatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return licenseDoc.id;
  } catch (error) {
    console.error('Error activating license:', error);
    throw error;
  }
};

// Wallet Management
export const getWalletBalance = async (uid: string): Promise<number> => {
  try {
    const userProfile = await getUserProfile(uid);
    return userProfile?.walletBalance || 0;
  } catch (error) {
    console.error('Error getting wallet balance:', error);
    throw error;
  }
};

export const getWalletTransactions = async (uid: string): Promise<WalletTransaction[]> => {
  try {
    const q = query(
      collection(db, 'walletTransactions'),
      where('userId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as WalletTransaction));
  } catch (error) {
    console.error('Error getting wallet transactions:', error);
    throw error;
  }
};

// Admin functions
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as User));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

export const getAllPromoCodes = async (): Promise<PromoCode[]> => {
  try {
    const q = query(collection(db, 'promoCodes'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as PromoCode;
    });
  } catch (error) {
    console.error('Error getting all promo codes:', error);
    throw error;
  }
};

export const getAllLicenses = async (): Promise<License[]> => {
  try {
    const q = query(collection(db, 'licenses'), orderBy('purchasedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        purchasedAt: data.purchasedAt?.toDate ? data.purchasedAt.toDate() : new Date(data.purchasedAt),
        activatedAt: data.activatedAt?.toDate ? data.activatedAt.toDate() : (data.activatedAt ? new Date(data.activatedAt) : undefined)
      } as License;
    });
  } catch (error) {
    console.error('Error getting all licenses:', error);
    throw error;
  }
};

// License Plan Management Functions
export const createLicensePlan = async (planData: Omit<LicensePlan, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const docRef = await addDoc(collection(db, 'licensePlans'), {
      ...planData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating license plan:', error);
    throw error;
  }
};

export const updateLicensePlan = async (planId: string, updates: Partial<LicensePlan>) => {
  try {
    const planRef = doc(db, 'licensePlans', planId);
    await updateDoc(planRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating license plan:', error);
    throw error;
  }
};

export const deleteLicensePlan = async (planId: string) => {
  try {
    const planRef = doc(db, 'licensePlans', planId);
    await deleteDoc(planRef);
  } catch (error) {
    console.error('Error deleting license plan:', error);
    throw error;
  }
};

export const getAllLicensePlans = async (): Promise<LicensePlan[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'licensePlans'));
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as LicensePlan;
    });
  } catch (error) {
    console.error('Error getting all license plans:', error);
    throw error;
  }
};

export const getActiveLicensePlans = async (): Promise<LicensePlan[]> => {
  try {
    const q = query(
      collection(db, 'licensePlans'),
      where('isActive', '==', true),
      orderBy('duration', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as LicensePlan;
    });
  } catch (error) {
    console.error('Error getting active license plans:', error);
    throw error;
  }
};

export const getLicensePlanById = async (planId: string): Promise<LicensePlan | null> => {
  try {
    const docRef = doc(db, 'licensePlans', planId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as LicensePlan;
    }
    return null;
  } catch (error) {
    console.error('Error getting license plan:', error);
    throw error;
  }
};

// Generate a dummy payment transaction ID for testing
export const generatePaymentTransactionId = (): string => {
  const prefix = 'PAY';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}_${timestamp}_${random}`;
};

// Comprehensive Promo Code Validation
export const validatePromoCodeForUser = async (
  code: string,
  userId: string
): Promise<{
  valid: boolean;
  message: string;
  discount: number;
  promoCode?: PromoCode;
}> => {
  try {
    if (!code.trim()) {
      return { valid: false, message: 'Please enter a promo code', discount: 0 };
    }

    const promoCode = await getPromoCode(code);
    
    if (!promoCode) {
      return { valid: false, message: 'Invalid promo code', discount: 0 };
    }

    if (!promoCode.isActive) {
      return { valid: false, message: 'Promo code is inactive', discount: 0 };
    }

    if (promoCode.usedCount >= promoCode.usageLimit) {
      return { valid: false, message: 'Promo code usage limit exceeded', discount: 0 };
    }

    if (new Date(promoCode.validUntil) < new Date()) {
      return { valid: false, message: 'Promo code has expired', discount: 0 };
    }

    if (promoCode.createdBy === userId) {
      return { valid: false, message: 'Cannot use your own promo code', discount: 0 };
    }

    // Check if user already used this promo code
    const existingUsageQuery = query(
      collection(db, 'promoCodeUsages'),
      where('promoCode', '==', promoCode.code),
      where('usedBy', '==', userId)
    );
    const existingUsage = await getDocs(existingUsageQuery);
    
    if (!existingUsage.empty) {
      return { valid: false, message: 'You have already used this promo code', discount: 0 };
    }

    return {
      valid: true,
      message: `Valid! ${promoCode.discountPercentage}% discount`,
      discount: promoCode.discountPercentage,
      promoCode
    };
  } catch (error) {
    console.error('Error validating promo code:', error);
    return {
      valid: false,
      message: 'Error validating promo code',
      discount: 0
    };
  }
};

// GST Number Validation
export const validateGSTNumber = (gstNumber: string): { valid: boolean; message: string } => {
  if (!gstNumber || gstNumber.trim() === '') {
    return { valid: false, message: 'GST Number is required' };
  }

  // Remove spaces and convert to uppercase
  const cleanGST = gstNumber.replace(/\s+/g, '').toUpperCase();
  
  // GST number should be 15 characters long
  if (cleanGST.length !== 15) {
    return { valid: false, message: 'GST Number must be exactly 15 characters long' };
  }

  // GST number pattern: 2 digits + 10 alphanumeric + 1 digit + 1 character + 1 digit
  const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  
  if (!gstPattern.test(cleanGST)) {
    return { valid: false, message: 'Invalid GST Number format. Example: 22AAAAA0000A1Z5' };
  }

  return { valid: true, message: 'Valid GST Number' };
};

// Check if user has already purchased a trial license
export const hasUserPurchasedTrialLicense = async (userId: string): Promise<boolean> => {
  try {
    // Get all user licenses
    const licensesQuery = query(
      collection(db, 'licenses'),
      where('purchasedBy', '==', userId)
    );
    const licensesSnapshot = await getDocs(licensesQuery);
    
    // Check if any license was purchased with a trial plan (price = 0)
    for (const licenseDoc of licensesSnapshot.docs) {
      const license = { id: licenseDoc.id, ...licenseDoc.data() } as License;
      if (license.pricePerLicense === 0) {
        return true; // User has already purchased a trial license
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking user trial license history:', error);
    return false; // Return false on error to not block legitimate purchases
  }
};

// Check if GST number has already been used for a trial license
export const hasGSTNumberUsedTrialLicense = async (gstNumber: string): Promise<{ used: boolean; userId?: string }> => {
  try {
    // Clean the GST number
    const cleanGST = gstNumber.replace(/\s+/g, '').toUpperCase();
    
    // Query all licenses with this GST number that were trial licenses (price = 0)
    const licensesQuery = query(
      collection(db, 'licenses'),
      where('gstNumber', '==', cleanGST),
      where('pricePerLicense', '==', 0)
    );
    const licensesSnapshot = await getDocs(licensesQuery);
    
    if (!licensesSnapshot.empty) {
      const firstTrialLicense = licensesSnapshot.docs[0].data() as License;
      return {
        used: true,
        userId: firstTrialLicense.purchasedBy
      };
    }
    
    return { used: false };
  } catch (error) {
    console.error('Error checking GST trial license usage:', error);
    return { used: false }; // Return false on error to not block legitimate purchases
  }
};

// Comprehensive license purchase validation
export const validateLicensePurchase = async (
  userId: string,
  gstNumber: string,
  isTrialPlan: boolean
): Promise<{ valid: boolean; message: string }> => {
  try {
    // 1. Validate GST Number format
    const gstValidation = validateGSTNumber(gstNumber);
    if (!gstValidation.valid) {
      return gstValidation;
    }

    // 2. If this is a trial plan (price = 0), perform additional validations
    if (isTrialPlan) {
      // Check if user has already purchased a trial license
      const userHasTrial = await hasUserPurchasedTrialLicense(userId);
      if (userHasTrial) {
        return {
          valid: false,
          message: 'You have already purchased a trial license. Trial licenses are limited to one per user.'
        };
      }

      // Check if this GST number has already been used for a trial license
      const gstTrialCheck = await hasGSTNumberUsedTrialLicense(gstNumber);
      if (gstTrialCheck.used) {
        if (gstTrialCheck.userId === userId) {
          return {
            valid: false,
            message: 'You have already used this GST number for a trial license.'
          };
        } else {
          return {
            valid: false,
            message: 'This GST number has already been used for a trial license by another user. Each GST number can only be used once for trial licenses.'
          };
        }
      }
    }

    return { valid: true, message: 'License purchase validation passed' };
  } catch (error) {
    console.error('Error validating license purchase:', error);
    return {
      valid: false,
      message: 'Error validating license purchase. Please try again.'
    };
  }
};

// Promo Code Usage Tracking
export const getPromoCodeUsageHistory = async (userId: string): Promise<PromoCodeUsage[]> => {
  try {
    const q = query(
      collection(db, 'promoCodeUsages'),
      where('createdBy', '==', userId),
      orderBy('usedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        promoCode: data.promoCode || '',
        usedBy: data.usedBy || '',
        createdBy: data.createdBy || '',
        discountAmount: data.discountAmount || 0,
        rewardAmount: data.rewardAmount || 0,
        orderId: data.orderId || '',
        // Convert Firestore Timestamps to JavaScript Dates
        usedAt: data.usedAt?.toDate ? data.usedAt.toDate() : new Date(data.usedAt)
      } as PromoCodeUsage;
    });
  } catch (error) {
    console.error('Error getting promo code usage history:', error);
    throw error;
  }
};

export const getPromoCodeEarningsStats = async (userId: string): Promise<{
  totalEarnings: number;
  totalUsages: number;
  thisMonthEarnings: number;
  thisMonthUsages: number;
}> => {
  try {
    const q = query(
      collection(db, 'promoCodeUsages'),
      where('createdBy', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    let totalEarnings = 0;
    let totalUsages = 0;
    let thisMonthEarnings = 0;
    let thisMonthUsages = 0;
    
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const usedAt = data.usedAt?.toDate ? data.usedAt.toDate() : new Date(data.usedAt);
      const earnings = data.rewardAmount || 0;
      
      totalEarnings += earnings;
      totalUsages += 1;
      
      if (usedAt >= firstDayOfMonth) {
        thisMonthEarnings += earnings;
        thisMonthUsages += 1;
      }
    });
    
    return {
      totalEarnings,
      totalUsages,
      thisMonthEarnings,
      thisMonthUsages
    };
  } catch (error) {
    console.error('Error getting promo code earnings stats:', error);
    throw error;
  }
};

// Admin function to update promo code settings
export const updatePromoCodeSettings = async (
  promoCodeId: string,
  updates: {
    discountPercentage?: number;
    rewardAmount?: number;
    usageLimit?: number;
    isActive?: boolean;
  },
  adminRole: string
) => {
  try {
    // Enforce admin-only access
    if (adminRole !== 'admin') {
      throw new Error('Only administrators can update promo code settings');
    }
    
    const promoRef = doc(db, 'promoCodes', promoCodeId);
    await updateDoc(promoRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return promoCodeId;
  } catch (error) {
    console.error('Error updating promo code settings:', error);
    throw error;
  }
};

export const getPromoCodeById = async (promoCodeId: string): Promise<PromoCode | null> => {
  try {
    const promoRef = doc(db, 'promoCodes', promoCodeId);
    const promoDoc = await getDoc(promoRef);
    
    if (promoDoc.exists()) {
      const data = promoDoc.data();
      return {
        id: promoDoc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as PromoCode;
    }
    return null;
  } catch (error) {
    console.error('Error getting promo code by ID:', error);
    throw error;
  }
};

// Update a promo code (including the code string itself)
export const updatePromoCode = async (
  promoCodeId: string,
  updates: {
    code?: string;
    discountPercentage?: number;
    rewardAmount?: number;
    usageLimit?: number;
    isActive?: boolean;
  },
  adminRole: string
) => {
  try {
    // Enforce admin-only access
    if (adminRole !== 'admin') {
      throw new Error('Only administrators can update promo codes');
    }
    
    // If updating the code, check if it's unique
    if (updates.code) {
      const isUnique = await validatePromoCodeUnique(updates.code, promoCodeId);
      if (!isUnique) {
        throw new Error('Promo code already exists');
      }
    }
    
    const promoRef = doc(db, 'promoCodes', promoCodeId);
    await updateDoc(promoRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return promoCodeId;
  } catch (error) {
    console.error('Error updating promo code:', error);
    throw error;
  }
};

// Validate if a promo code is unique (excluding a specific promo code ID)
export const validatePromoCodeUnique = async (
  code: string,
  excludeId?: string
): Promise<boolean> => {
  try {
    const q = query(
      collection(db, 'promoCodes'),
      where('code', '==', code)
    );
    
    const querySnapshot = await getDocs(q);
    
    // If no documents found, code is unique
    if (querySnapshot.empty) {
      return true;
    }
    
    // If we're excluding a specific ID (for updates), check if any other codes match
    if (excludeId) {
      return !querySnapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    // If no exclusion, any match means not unique
    return false;
  } catch (error) {
    console.error('Error validating promo code uniqueness:', error);
    throw error;
  }
};

// Create promo code for regular users (non-admin)
export const createUserPromoCode = async (
  promoData: Omit<PromoCode, 'id' | 'createdAt' | 'updatedAt' | 'rewardAmount'>
) => {
  try {
    const promoRef = await addDoc(collection(db, 'promoCodes'), {
      ...promoData,
      rewardAmount: 50, // Fixed reward amount for regular users
      isDefault: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return promoRef.id;
  } catch (error) {
    console.error('Error creating user promo code:', error);
    throw error;
  }
};

// Admin Settings Management
export const getAdminSettings = async (): Promise<AdminSettings | null> => {
  try {
    const settingsRef = doc(db, 'adminSettings', 'default');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      return {
        id: settingsDoc.id,
        ...data,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as AdminSettings;
    } else {
      // Create default settings if they don't exist
      const defaultSettings = {
        defaultPromoDiscount: 5,
        defaultPromoReward: 50,
        defaultPromoUsageLimit: 100,
        defaultPromoValidityDays: 365,
        updatedAt: serverTimestamp(),
        updatedBy: 'system'
      };
      
      await setDoc(settingsRef, defaultSettings);
      return {
        id: 'default',
        defaultPromoDiscount: 5,
        defaultPromoReward: 50,
        defaultPromoUsageLimit: 100,
        defaultPromoValidityDays: 365,
        updatedAt: new Date(),
        updatedBy: 'system'
      };
    }
  } catch (error) {
    console.error('Error getting admin settings:', error);
    return null;
  }
};

export const updateAdminSettings = async (
  settings: Partial<AdminSettings>,
  adminUserId: string,
  adminRole: string
): Promise<void> => {
  try {
    if (adminRole !== 'admin') {
      throw new Error('Only administrators can update system settings');
    }

    const settingsRef = doc(db, 'adminSettings', 'default');
    await updateDoc(settingsRef, {
      ...settings,
      updatedAt: serverTimestamp(),
      updatedBy: adminUserId
    });
  } catch (error) {
    console.error('Error updating admin settings:', error);
    throw error;
  }
};


// Utility Functions
export const generateDefaultUserCode = (userId: string): string => {
  // Create a deterministic promo code based on user ID
  const shortId = userId.substring(0, 8).toUpperCase();
  const hash = userId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  const suffix = Math.abs(hash).toString(36).substring(0, 4).toUpperCase();
  return `USER${shortId}${suffix}`;
};

export const generateLicenseKey = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const sections = 4;
  const sectionLength = 4;
  const key = [];
  
  for (let i = 0; i < sections; i++) {
    let section = '';
    for (let j = 0; j < sectionLength; j++) {
      section += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    key.push(section);
  }
  
  return key.join('-');
};

export const generatePromoCode = (prefix: string = 'PROMO', length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = prefix;
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Admin User Management Functions
export const makeUserAdmin = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'admin',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error making user admin:', error);
    throw error;
  }
};

export const makeUserRegular = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'user',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error making user regular:', error);
    throw error;
  }
};

// User activation/deactivation functions
export const deactivateUser = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

export const activateUser = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: true,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error activating user:', error);
    throw error;
  }
};

// License activation/deactivation functions
export const suspendLicense = async (licenseId: string): Promise<void> => {
  try {
    const licenseRef = doc(db, 'licenses', licenseId);
    await updateDoc(licenseRef, {
      status: 'suspended',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error suspending license:', error);
    throw error;
  }
};

export const unsuspendLicense = async (licenseId: string): Promise<void> => {
  try {
    const licenseRef = doc(db, 'licenses', licenseId);
    await updateDoc(licenseRef, {
      status: 'active',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error unsuspending license:', error);
    throw error;
  }
};

// Support Ticket Management
export const createSupportTicket = async (
  ticketData: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt' | 'responses'>
) => {
  try {
    // Remove undefined fields to prevent Firestore errors
    const cleanTicketData = Object.fromEntries(
      Object.entries(ticketData).filter(([, value]) => value !== undefined)
    );
    
    const docRef = await addDoc(collection(db, 'supportTickets'), {
      ...cleanTicketData,
      responses: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating support ticket:', error);
    throw error;
  }
};

export const getSupportTicketsByUser = async (userId: string): Promise<SupportTicket[]> => {
  try {
    const q = query(
      collection(db, 'supportTickets'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt),
        // Handle responses array
        responses: (data.responses || []).map((response: unknown) => {
          const respData = response as { [key: string]: unknown };
          const createdAt = respData.createdAt as { toDate?: () => Date } | Date | undefined;
          return {
            ...respData,
            createdAt: createdAt && typeof createdAt === 'object' && 'toDate' in createdAt && createdAt.toDate 
              ? createdAt.toDate() 
              : new Date((createdAt as Date) || new Date())
          } as SupportResponse;
        })
      } as SupportTicket;
    });
  } catch (error) {
    console.error('Error getting support tickets by user:', error);
    throw error;
  }
};

export const getAllSupportTickets = async (): Promise<SupportTicket[]> => {
  try {
    const q = query(
      collection(db, 'supportTickets'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt),
        // Handle responses array
        responses: (data.responses || []).map((response: unknown) => {
          const respData = response as { [key: string]: unknown };
          const createdAt = respData.createdAt as { toDate?: () => Date } | Date | undefined;
          return {
            ...respData,
            createdAt: createdAt && typeof createdAt === 'object' && 'toDate' in createdAt && createdAt.toDate 
              ? createdAt.toDate() 
              : new Date((createdAt as Date) || new Date())
          } as SupportResponse;
        })
      } as SupportTicket;
    });
  } catch (error) {
    console.error('Error getting all support tickets:', error);
    throw error;
  }
};

export const updateSupportTicketStatus = async (
  ticketId: string,
  status: SupportTicket['status'],
  adminRole?: string
) => {
  try {
    if (adminRole && adminRole !== 'admin') {
      throw new Error('Only administrators can update ticket status');
    }
    
    const ticketRef = doc(db, 'supportTickets', ticketId);
    await updateDoc(ticketRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating support ticket status:', error);
    throw error;
  }
};

export const addSupportTicketResponse = async (
  ticketId: string,
  response: Omit<SupportResponse, 'id' | 'ticketId' | 'createdAt'>
) => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);
    
    if (!ticketDoc.exists()) {
      throw new Error('Support ticket not found');
    }
    
    const ticketData = ticketDoc.data() as SupportTicket;
    const newResponse: SupportResponse = {
      id: `resp_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      ticketId,
      ...response,
      createdAt: new Date()
    };
    
    const updatedResponses = [...(ticketData.responses || []), newResponse];
    
    await updateDoc(ticketRef, {
      responses: updatedResponses,
      status: response.responderRole === 'admin' ? 'in_progress' : ticketData.status,
      updatedAt: serverTimestamp()
    });
    
    return newResponse.id;
  } catch (error) {
    console.error('Error adding support ticket response:', error);
    throw error;
  }
};

export const getUserDetailsForAdmin = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        uid: userDoc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as User;
    }
    return null;
  } catch (error) {
    console.error('Error getting user details for admin:', error);
    throw error;
  }
};

export const getUserLicensesForAdmin = async (userId: string): Promise<License[]> => {
  try {
    const q = query(
      collection(db, 'licenses'),
      where('purchasedBy', '==', userId),
      orderBy('purchasedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        purchasedAt: data.purchasedAt?.toDate ? data.purchasedAt.toDate() : new Date(data.purchasedAt),
        validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : new Date(data.validUntil),
        activatedAt: data.activatedAt?.toDate ? data.activatedAt.toDate() : data.activatedAt ? new Date(data.activatedAt) : undefined
      } as License;
    });
  } catch (error) {
    console.error('Error getting user licenses for admin:', error);
    throw error;
  }
};
